/**
 * Mesure cote client : la vue de page et le clic sur un bouton d'accueil.
 *
 * Ce sont les deux seuls evenements qui PRECEDENT la connexion WebSocket, et
 * donc les deux seuls qui doivent passer par HTTP. Tout ce qui concerne une
 * partie est emis par le serveur, qui fait autorite : un client ne s'auto-
 * declare pas vainqueur.
 *
 * Deux identifiants, deux durees de vie, et la difference compte :
 *
 *  - la SESSION vit dans sessionStorage : elle meurt avec l'onglet. Elle sert a
 *    relier une vue de page au clic qui la suit, rien de plus.
 *  - le VISITEUR vit dans localStorage : c'est lui, et lui seul, qui permet de
 *    savoir si quelqu'un revient le lendemain. C'est aussi la seule partie du
 *    dispositif qui merite une ligne dans la page de confidentialite, et elle y
 *    est.
 *
 * Les deux sont des tirages au hasard. Ils ne portent rien : ni pseudo, ni
 * adresse, ni empreinte de navigateur. Rien n'est envoye a un tiers, tout va au
 * serveur du jeu.
 */
import type { ClientEvent, EventProps } from '@retusch/shared';
import { serverHttpBase } from './net/connection.js';

const SESSION_KEY = 'retusch:measure-session';
const VISITOR_KEY = 'retusch:measure-visitor';

/** 16 octets hexadecimaux : assez pour ne pas collisionner, trop peu pour dire quoi que ce soit. */
function randomId(): string {
  const bytes = new Uint8Array(8);
  crypto.getRandomValues(bytes);
  return [...bytes].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

/**
 * Lit ou cree un identifiant. Toute erreur de stockage (navigation privee
 * stricte, cookies bloques) rend null : on mesure alors sans lui, plutot que de
 * casser la page pour une statistique.
 */
function readId(storage: 'session' | 'local', key: string): string | null {
  try {
    const store = storage === 'session' ? window.sessionStorage : window.localStorage;
    const existing = store.getItem(key);
    if (existing !== null && /^[a-f0-9]{16}$/.test(existing)) return existing;
    const created = randomId();
    store.setItem(key, created);
    return created;
  } catch {
    return null;
  }
}

let session: string | null = null;

/** Envoie un evenement. Jamais bloquant, jamais attendu, jamais relance. */
export function track(type: ClientEvent, props: EventProps = {}): void {
  if (typeof window === 'undefined') return;
  session ??= readId('session', SESSION_KEY);
  if (session === null) return;

  const body = JSON.stringify({
    type,
    session,
    visitor: readId('local', VISITOR_KEY),
    props,
  });

  try {
    // `sendBeacon` survit a la fermeture de l'onglet, la ou un fetch serait
    // annule. Type `text/plain` : c'est une requete simple, donc sans preflight
    // vers l'autre origine du serveur de jeu.
    const blob = new Blob([body], { type: 'text/plain;charset=UTF-8' });
    const sent = navigator.sendBeacon?.(`${serverHttpBase()}/e`, blob) ?? false;
    if (sent) return;
  } catch {
    // Beacon indisponible : on tente le repli ci-dessous.
  }

  // Repli discret, sans attendre la reponse ni signaler l'echec.
  void fetch(`${serverHttpBase()}/e`, {
    method: 'POST',
    body,
    headers: { 'content-type': 'text/plain;charset=UTF-8' },
    keepalive: true,
    mode: 'no-cors',
  }).catch(() => undefined);
}

/** Le canal d'ou vient le visiteur : ?ref=itch, ?ref=reddit... */
function referrerTag(): string {
  const tag = new URLSearchParams(window.location.search).get('ref');
  if (tag === null) return 'direct';
  // Etiquette courte et sans surprise : le serveur refuserait le reste, autant
  // ne pas l'envoyer.
  const clean = tag.toLowerCase().replace(/[^a-z0-9_-]/g, '').slice(0, 24);
  return clean === '' ? 'direct' : clean;
}

let viewed = false;

/** Une vue de page, une seule fois par chargement. */
export function trackPageView(): void {
  if (viewed) return;
  viewed = true;
  track('page_view', {
    ref: referrerTag(),
    lang: (navigator.language ?? 'xx').slice(0, 5),
    // Grossier et suffisant : on cherche a savoir si l'on dessine au doigt.
    device: window.matchMedia('(hover: none)').matches ? 'touch' : 'desktop',
  });
}

/** Un clic sur l'une des quatre intentions de l'accueil. */
export function trackCta(cta: 'quick_play' | 'create' | 'join' | 'browse'): void {
  track('cta_click', { cta });
}
